import { cn } from '@/platform/lib/utils';
import useDebounce from '@/react/hooks/useDebounce';
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
    Description,
    Field,
    Label,
} from '@headlessui/react';
import {
    CheckIcon,
    ChevronUpDownIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/16/solid';
import { router, usePage } from '@inertiajs/react';
import { useEffect, useId, useState } from 'react';

const MIN_SEARCH_LENGTH = 3;

const CustomerAutocomplete = ({
    label,
    description,
    error,
    onChange,
    required = false,
    disabled = false,
    reset = [],
}) => {
    let uuid = useId();

    const { autocomplete_customer, autocomplete_customers } = usePage().props;
    const [query, setQuery] = useState('');

    const debouncedQuery = useDebounce(query);

    const handleChange = option => {
        if (option.id === autocomplete_customer?.id) {
            return;
        }

        onChange?.(option.id);

        router.get(
            window.location.href,
            { ac_customer_id: option.id },
            {
                preserveState: true,
                preserveScroll: true,
                replace: true,
                only: ['autocomplete_customer', ...reset],
            }
        );
    };

    useEffect(() => {
        if (debouncedQuery.length >= MIN_SEARCH_LENGTH) {
            router.get(
                window.location.href,
                { ac_search: debouncedQuery },
                {
                    preserveState: true,
                    preserveScroll: true,
                    replace: true,
                    only: ['autocomplete_customers'],
                }
            );
        }
    }, [debouncedQuery]);

    return (
        <Field disabled={disabled}>
            {label && (
                <Label
                    htmlFor={uuid}
                    className='mb-1 block text-sm/6 font-medium text-gray-900'
                >
                    {label}
                    {required && (
                        <span className='inline-block pl-1 text-red-800'>
                            *
                        </span>
                    )}
                </Label>
            )}
            {description && (
                <Description className='mb-1 text-sm/6 text-gray-500'>
                    {description}
                </Description>
            )}
            <Combobox
                immediate
                by='id'
                value={autocomplete_customer || []}
                onChange={handleChange}
                onClose={() => setQuery('')}
                invalid={!!error}
            >
                <div className='relative'>
                    <div
                        className={cn(
                            'focus-within:outline-primary-600 flex w-full items-center rounded-md bg-white py-1.5 pr-8 pl-3 outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 has-disabled:cursor-not-allowed has-disabled:bg-gray-50',
                            error &&
                                'outline-red-300 focus-within:outline-red-600'
                        )}
                    >
                        <MagnifyingGlassIcon
                            aria-hidden='true'
                            className='mr-2 size-5 shrink-0 text-gray-400 sm:size-4'
                        />
                        <ComboboxInput
                            id={uuid}
                            required={required}
                            placeholder='Zoek klant...'
                            displayValue={value => value?.name ?? ''}
                            onChange={event => setQuery(event.target.value)}
                            className={cn(
                                'min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-gray-900 ring-0 placeholder:text-gray-400 focus:ring-0 focus:outline-none disabled:cursor-not-allowed disabled:text-gray-500 sm:text-sm/6',
                                error && 'text-red-900 placeholder:text-red-300'
                            )}
                        />
                    </div>
                    <ComboboxButton className='absolute inset-y-0 right-0 flex items-center pr-2'>
                        <ChevronUpDownIcon
                            aria-hidden='true'
                            className='size-5 text-gray-500 sm:size-4'
                        />
                    </ComboboxButton>
                    <ComboboxOptions className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 empty:invisible sm:text-sm/6'>
                        {autocomplete_customers?.length === 0 ? (
                            <div className='px-3 py-1.5 text-gray-500'>
                                Geen klanten gevonden
                            </div>
                        ) : (
                            autocomplete_customers?.map(option => (
                                <ComboboxOption
                                    key={option.id}
                                    value={option}
                                    className='group data-focus:bg-primary-600 flex cursor-default items-center justify-between px-3 py-1.5 text-gray-900 transition-colors select-none data-focus:text-white'
                                >
                                    <span className='block truncate group-data-selected:font-semibold'>
                                        {option.name}
                                    </span>
                                    <CheckIcon
                                        aria-hidden='true'
                                        className='text-primary-600 invisible size-4 group-data-focus:text-white group-data-selected:visible'
                                    />
                                </ComboboxOption>
                            ))
                        )}
                    </ComboboxOptions>
                </div>
                {error && (
                    <Description className='mt-1 text-sm text-red-600'>
                        {error}
                    </Description>
                )}
            </Combobox>
        </Field>
    );
};

export default CustomerAutocomplete;
